import type {
  PaycoreRequestPaymentResponse,
  SolidgateRequestPaymentResponse,
  SolidgatePaymentStatusResponse,
  SolidgatePaymentCardDetails,
} from '@/types/payment';

import { FEATURE } from '@/config/features';
import { trackEvent } from '@/helpers/gtag';
import { emptyApi, getTags } from '@/store/emptyApi';
import {
  IAssociationOrderCustomerFile,
  IOrderAppearanceHideRequest,
  IOrderAppearanceUnhideRequest,
  IOrderCreatePayload,
  IOrderCreateResponse,
  IOrderCustomerExternalCredentialPatchRequest,
  IOrderCustomerFilePatchRequest,
  IOrderDetails,
  IOrderProgressStatuses,
  IOrderSandboxCreateRequest,
  IOrderSandboxCreateResponse,
  IOrderSandboxUpdateRequest,
  IOrderSandboxUpdateResponse,
  IOrderSearchItem,
  IOrderSearchParams,
  TOrderAExternalCredentials,
} from '@/types/order';
import { SearchView } from '@/types/search';
import { ITicket } from '@/types/ticket';

const { type, path } = FEATURE.Order;
const { invalidatesTags, providesTags } = getTags(type, [
  FEATURE.Revision.type,
  FEATURE.Ticket.type,
  FEATURE.OwnFile.type,
  FEATURE.CustomerExternalCredential.type,
]);

export const orderApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    // Search
    searchOrders: build.query<SearchView<IOrderSearchItem>, IOrderSearchParams>({
      query: (rawParams) => {
        const params = { ...rawParams };
        const statuses = rawParams?.idProgressStatus || [];

        if (statuses.includes(IOrderProgressStatuses.Pending)) {
          params.idProgressStatus = [...statuses, IOrderProgressStatuses.UpForGrabs];
        }
        if (statuses.includes(IOrderProgressStatuses.InWork)) {
          params.idProgressStatus = [...statuses, IOrderProgressStatuses.Submitted];
        }

        return { url: path, params };
      },
      providesTags: () => [{ type, id: 'search' }],
    }),
    createOrder: build.mutation<IOrderCreateResponse, IOrderCreatePayload>({
      query: (body) => {
        return {
          url: path,
          method: 'POST',
          body,
        };
      },
      async onQueryStarted(_, { queryFulfilled }) {
        try {
          await queryFulfilled;
          trackEvent({ action: 'Created', category: 'Order' });
        } catch (err) {}
      },
      invalidatesTags,
    }),
    createOrderSandbox: build.mutation<IOrderSandboxCreateResponse, IOrderSandboxCreateRequest>({
      query: ({ body, id }) => {
        return {
          url: `${path}/${id}/sandbox/create`,
          method: 'POST',
          body,
        };
      },
      invalidatesTags,
    }),
    updateOrderSandbox: build.mutation<IOrderSandboxUpdateResponse, IOrderSandboxUpdateRequest>({
      query: ({ body, id }) => {
        return {
          url: `${path}/${id}/sandbox/update`,
          method: 'PUT',
          body,
        };
      },
      invalidatesTags,
    }),
    removeOrderSandboxDiscount: build.mutation<IOrderSandboxUpdateResponse, number>({
      query: (id) => {
        return {
          url: `${path}/${id}/sandbox/discount`,
          method: 'DELETE',
        };
      },
    }),
    getOrder: build.query<IOrderDetails, number>({
      query: (id) => ({ url: `${path}/${id}` }),
      providesTags,
    }),
    // Pay order
    // -- Paycore
    payForOrderWithPaycore: build.mutation<PaycoreRequestPaymentResponse, number>({
      query: (id) => ({
        url: `/order-snapshots/${id}/pay-for-snapshot`,
        method: 'POST',
      }),
      async onQueryStarted(id, { queryFulfilled }) {
        try {
          await queryFulfilled;
          trackEvent({ action: 'add_to_cart', category: 'Order', label: `Order id ${id}` }); // -> next tracking "trackPurchase"
        } catch (err) {}
      },
    }),
    payForOrderExtraSnapshotWithPaycore: build.mutation<PaycoreRequestPaymentResponse, number>({
      query: (id) => ({
        url: `/order-snapshots/${id}/pay-for-extra`,
        method: 'POST',
      }),
    }),
    // -- Solidgate (new)
    payForOrderWithSolidgate: build.mutation<SolidgateRequestPaymentResponse, number>({
      query: (id) => ({
        url: `/order-snapshots/${id}/solidgate/payment-form/pay-for-snapshot`,
        method: 'POST',
      }),
    }),
    payForOrderExtraWithSolidgate: build.mutation<SolidgateRequestPaymentResponse, number>({
      query: (id) => ({
        url: `/order-snapshots/${id}/solidgate/payment-form/pay-for-extra`,
        method: 'POST',
      }),
    }),
    // NOTE: this mutation should be used as long polling to check the payment status
    checkSolidgatePaymentStatus: build.mutation<SolidgatePaymentStatusResponse, { idPayment: string }>({
      query: ({ idPayment }) => ({
        url: `/order-snapshots/solidgate/payments/${idPayment}/status`,
        method: 'POST',
      }),
      invalidatesTags: () => [{ type: 'SolidgatePayments' }],
    }),
    // Get previously saved Solidgate cards
    getSolidgatePaymentCards: build.query<SolidgatePaymentCardDetails[], void>({
      query: () => ({
        url: `/order-snapshots/solidgate/payments/cards`,
      }),
      providesTags: () => [{ type: 'SolidgatePayments' }],
    }),
    /**
     * Backend save card for Solidgate payment by default.
     * That's why this mutation is used to cancel saving card.
     * Execute only after checkSolidgatePaymentStatus returns 'SUCCESS'
     */
    saveSolidgatePaymentCard: build.mutation<void, { idPayment: string; shouldSaveCard: boolean; isPrimary: boolean }>({
      query: ({ idPayment, ...body }) => ({
        url: `/order-snapshots/solidgate/payments/${idPayment}/card`,
        body,
        method: 'PATCH',
      }),
      invalidatesTags: () => [{ type: 'SolidgatePayments' }],
    }),
    // Remove previously saved card
    removeSolidgatePaymentCard: build.mutation<void, string[]>({
      query: (body) => ({
        url: `/order-snapshots/solidgate/payments/cards/delete`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: () => [{ type: 'SolidgatePayments' }],
    }),
    markSolidgatePaymentCardAsPrimary: build.mutation<void, string>({
      query: (id) => ({
        url: `/order-snapshots/solidgate/payments/cards/${id}/primary`,
        method: 'PATCH',
      }),
      invalidatesTags: () => [{ type: 'SolidgatePayments' }],
    }),

    hideOrder: build.mutation<unknown, IOrderAppearanceHideRequest>({
      query: ({ id, body }) => ({ url: `${path}/${id}/appearance/hide`, method: 'PATCH', body }),
      invalidatesTags,
    }),
    unhideOrder: build.mutation<unknown, IOrderAppearanceUnhideRequest>({
      query: ({ id, body }) => ({ url: `${path}/${id}/appearance/unhide`, method: 'PATCH', body }),
      invalidatesTags,
    }),

    // Associations
    getOrderTickets: build.query<ITicket[], number>({
      query: (id) => ({ url: `${path}/${id}/tickets`, method: 'GET' }),
      providesTags: () => [{ type: FEATURE.Ticket.type }],
    }),
    // Files
    getOrderACustomerFiles: build.query<IAssociationOrderCustomerFile[], number>({
      query: (id) => ({ url: `${path}/${id}/a/customer-files`, method: 'GET' }),
      providesTags: () => [{ type: FEATURE.OwnFile.type }],
    }),
    claimOrderACustomerFiles: build.mutation<IAssociationOrderCustomerFile[], IOrderCustomerFilePatchRequest>({
      query: ({ id, body }) => ({ url: `${path}/${id}/a/customer-files/claim`, method: 'PUT', body }),
      invalidatesTags,
    }),
    withdrawOrderACustomerFiles: build.mutation<IAssociationOrderCustomerFile[], IOrderCustomerFilePatchRequest>({
      query: ({ id, body }) => ({ url: `${path}/${id}/a/customer-files/withdraw`, method: 'DELETE', body }),
      invalidatesTags,
    }),
    // Customer External Credentials
    claimCustomerExternalCredential: build.mutation<TOrderAExternalCredentials[], IOrderCustomerExternalCredentialPatchRequest>({
      query: ({ id, body }) => ({ url: `${path}/${id}/a/customer-external-credentials/claim`, method: 'PUT', body }),
      invalidatesTags,
    }),
    withdrawAssociationCustomerExternalCredential: build.mutation<
      TOrderAExternalCredentials[],
      IOrderCustomerExternalCredentialPatchRequest
    >({
      query: ({ id, body }) => ({ url: `${path}/${id}/a/customer-external-credentials/withdraw`, method: 'DELETE', body }),
      invalidatesTags,
    }),
  }),
  overrideExisting: false,
});

export const {
  useSearchOrdersQuery,
  useCreateOrderMutation,
  useCreateOrderSandboxMutation,
  useUpdateOrderSandboxMutation,
  useGetOrderQuery,
  useHideOrderMutation,
  useUnhideOrderMutation,

  // Payments
  usePayForOrderWithPaycoreMutation,
  usePayForOrderExtraSnapshotWithPaycoreMutation,
  usePayForOrderWithSolidgateMutation,
  usePayForOrderExtraWithSolidgateMutation,
  useCheckSolidgatePaymentStatusMutation,
  // Solidgate cards
  useGetSolidgatePaymentCardsQuery,
  useSaveSolidgatePaymentCardMutation,
  useRemoveSolidgatePaymentCardMutation,
  useMarkSolidgatePaymentCardAsPrimaryMutation,

  useGetOrderACustomerFilesQuery,
  useClaimOrderACustomerFilesMutation,
  useWithdrawOrderACustomerFilesMutation,
  useGetOrderTicketsQuery,
  useLazyGetOrderTicketsQuery,
  useClaimCustomerExternalCredentialMutation,
  useRemoveOrderSandboxDiscountMutation,
  useWithdrawAssociationCustomerExternalCredentialMutation,
} = orderApi;
